import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch, put } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { useAuth } from "@/lib/auth";
import { API_BASE } from "@/lib/utils";

export type SetupJourneyStatus = "in_progress" | "completed" | "skipped";

export interface SetupJourney {
  /** The org the server resolved this journey for, or null when it could not.
   *  A null here is NOT an error response — see shouldSendToSetup. */
  orgId: string | null;
  status: SetupJourneyStatus;
  /** 1 = Connect, 2 = Embed, 3 = Verify. */
  step: number;
  connectorIds: string[];
  useCase: string | null;
  widgetId: string | null;
  completedAt: string | null;
}

const FALLBACK: SetupJourney = {
  orgId: null,
  status: "in_progress",
  step: 1,
  connectorIds: [],
  useCase: null,
  widgetId: null,
  completedAt: null,
};

/**
 * Org-scoped state of the guided setup journey, stored on
 * org_configs.config.setupJourney. Drives both the wizard itself and the
 * first-login redirect in App.tsx.
 *
 * A failed read resolves to "completed" rather than the default: the only
 * thing this state gates is whether to *interrupt* someone with a wizard, and
 * a transient API error is no reason to drop an established account back into
 * onboarding.
 */
export function useSetupJourney(opts?: { enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery<SetupJourney>({
    queryKey: keys.onboarding.setup(orgId),
    queryFn: async () => {
      try {
        return await apiFetch<SetupJourney>(`${API_BASE}/api/v1/onboarding/setup`);
      } catch (err) {
        if (err instanceof ApiError) return { ...FALLBACK, status: "completed" };
        throw err;
      }
    },
    enabled: (opts?.enabled ?? true) && !!orgId,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSaveSetupJourney() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useMutation({
    mutationFn: (patch: Partial<SetupJourney>) =>
      apiFetch<SetupJourney>(`${API_BASE}/api/v1/onboarding/setup`, put(patch)),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.onboarding.setup(orgId), data);
    },
  });
}
